<script>
  import {
    unique_classes, 
    render_micro_markdown,
    trim_whitespaces,
    handle_paste_and_convert_urls,
    wrap_selected_text,
  } from '#lib/util.js';

  const VARIANTS_TO_TAG_STYLES_AND_CLASS = {
    heading1:           { css_class: 'text-heading1',   tag: 'h1' },
    heading2:           { css_class: 'text-heading2',   tag: 'h2' },
    heading3:           { css_class: 'text-heading3',   tag: 'h3' },
    preamble:           { css_class: 'text-preamble',   tag: 'p' },
    body:               { css_class: 'text-body',       tag: 'p' },
    control:            { css_class: 'text-control',    tag: 'p' },
    definition_title:   { css_class: 'text-body',       tag: 'dt' },
    definition_details: { css_class: 'text-body',       tag: 'dd' },
    caption:            { css_class: 'text-caption',    tag: 'p' },
  };

  let {
    /** @type {'heading1'|'heading2'|'heading3'|'body'|'preamble'|'caption'|'control'} */
    variant = 'body',
    /** @type {string} */
    class: css_class,
    /** @type {string} */
    content = $bindable(),
    /** @type {contentEditable} */
    ref = $bindable(),
    /** @type {boolean} */
    editable = false,
    show_grid = true,
    onkeydown,
    onenter,
    placeholder,
    /** @type {string} name of the field to edit (required for Selection API) */
    field_name,
    onfocus,
    onblur,
    ...rest
  } = $props();

  let class_names = $derived(unique_classes(
    'text',
    'leading-trim',
    css_class,
    VARIANTS_TO_TAG_STYLES_AND_CLASS?.[variant]?.css_class,
    editable && 'editable',
    (editable && show_grid) && 'baseline-grid',
  ));

  /** Rendered as a ::before on the field itself, so it picks up the field's own
   *  typography, alignment and box without a second element to keep in sync. */
  let placeholder_text = $derived((!content && placeholder) ? placeholder : null);

  let html_tag = $derived(VARIANTS_TO_TAG_STYLES_AND_CLASS[variant]?.tag)

  function handle_blur(event) {
    // Multiple fields can share this binding (for example, entry preview labels).
    // Only the field finishing an edit may trim it: an unfocused copy reacting to
    // input would remove spaces and reset the active field's caret mid-sentence.
    if (editable && content !== undefined) {
      content = trim_whitespaces(content);
    }
    onblur?.(event);
  }

  function handle_key_down(event) {
    const is_cmd = /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? event.metaKey : event.ctrlKey;

    if (event.key === 'Enter' && !event.shiftKey) {
      onenter?.(event);
      return;
    }

    // TODO: Move this logic to the parent context (e.g. DynamicIsland for Container Text Blocks)
    if (is_cmd) {
      const key = event.key.toLowerCase();
      if (key === 'b') {
        event.preventDefault();
        wrap_selected_text(ref, '**');  // Cmd/Ctrl + B (Bold)
      } else if (key === 'i') {
        event.preventDefault();
        wrap_selected_text(ref, '*');   // Cmd/Ctrl + I (Italic)
      } else if (key === 'u') {
        event.preventDefault();
        wrap_selected_text(ref, '++');  // Cmd/Ctrl + U (Uppercase)
      } else if (key === 'h' && event.shiftKey) {
        event.preventDefault();
        wrap_selected_text(ref, '==');  // Cmd/Ctrl + Shift + H (Highlight)
      }
    }

    onkeydown?.(event);
  }
</script>


<!-- NOTE about Webkit Bug, 
  In certain constellations, e.g. text is centered, a click outside the contenteditable element, 
  will still focus the contenteditable element.
  But for example the user’s intention was to select the parent Block instead of the text.
  Learn more: https://stackoverflow.com/questions/34354085/clicking-outside-a-contenteditable-div-stills-give-focus-to-it
-->

{#if editable}
  <!-- TMP bug fix: not using sevelte:element here, as it causes a delay in rendering in combination with bind:innerText -->
  <div
    class={class_names}
    contenteditable="true"
    bind:innerText={content}
    bind:this={ref}
    tabindex="0"
    role="textbox"
    onpaste={(event)=>{handle_paste_and_convert_urls(event, ref)}}
    onkeydown={handle_key_down}
    {onfocus}
    onblur={handle_blur}
    data-field-name={field_name}
    data-placeholder={placeholder_text}
    {...rest}
  >
  </div>
{:else}
  <!-- One static tag per variant, not <svelte:element>: Svelte re-inserts a svelte:element while
       hydrating, which restarts every CSS animation on it (the page intro sweeps titles in) -->
  {@const html = render_micro_markdown(content || '', [], `text-${variant}`)}
  {#if html_tag === 'h1'}
    <h1 class={class_names} {...rest}>{@html html}</h1>
  {:else if html_tag === 'h2'}
    <h2 class={class_names} {...rest}>{@html html}</h2>
  {:else if html_tag === 'h3'}
    <h3 class={class_names} {...rest}>{@html html}</h3>
  {:else if html_tag === 'p'}
    <p class={class_names} {...rest}>{@html html}</p>
  {:else if html_tag === 'dt'}
    <dt class={class_names} {...rest}>{@html html}</dt>
  {:else if html_tag === 'dd'}
    <dd class={class_names} {...rest}>{@html html}</dd>
  {/if}
{/if}

<style lang="postcss">
  .text {
    width: 100%;
    white-space: pre-line;
    overflow-wrap: anywhere;
    box-sizing: content-box;
    text-wrap: balance;
    &:not(.editable):empty {
      display: none;
    }
    /* Prevent layout shift, only apply in editable mode */
    &.editable:empty:not([data-placeholder]) {
      /* Fix incorrect placement of caret if contenteditable is empty (Chrome Bug) */
      /* https://stackoverflow.com/a/65598623/6372711 */
      line-height: 1em;
    }
    /* The placeholder already marks the field as empty, so skip the block highlight */
    &.editable:empty:not(:focus):not(:active):not(:hover):not([data-placeholder]) {
      background-color: oklch(from var(--color-body, var(--color-default-foreground)) l c h / 0.1);
    }
    /* Placeholder: inline generated content, so it sits in the field's own box and
       inherits its typography and alignment. Keeping it in flow means the box is
       already the right height, so typing the first character shifts nothing.
       We switch from ::before to ::after on focus so the caret is always before
       the placeholder text (technique from svedit's TextProperty). */
    &.editable[data-placeholder]:not(:focus)::before,
    &.editable[data-placeholder]:focus::after {
      content: attr(data-placeholder);
      opacity: 0.32;
      font-style: italic;
      pointer-events: none;
    }
    /* Chrome and Firefox align the native caret to the bottom of an empty
       contenteditable instead of the text box, so we hide it and draw our own at
       cap height. Zero width and a capped height keep it out of layout. */
    &.editable[data-placeholder]:focus {
      caret-color: transparent;
      &::before {
        content: '';
        display: inline-block;
        width: 0;
        height: 1cap;
        box-shadow:
          0 -0.4cap 0 0.65px var(--editing, AccentColor),
          0 0 0 0.65px var(--editing, AccentColor),
          0 0.4cap 0 0.65px var(--editing, AccentColor);
        animation: text-caret-blink 1.1s ease-in-out infinite;
      }
    }
    &.editable {
      /* make sure fields have a min width for visibility in flex layouts, as intrinsic size can be zero if empty */
      min-width: max(150px, 5ch);
      /* Disable pretty text wrapping in edit mode */
      text-wrap: wrap;
      /* Disable text transforms in edit mode + when focused */
      &:focus {
        /* this causes too much flickering in the layout */
        text-transform: none;
      }
    }
  }
  :where(.text) {
    text-align: start;
  }

  @keyframes text-caret-blink {
    0%, 60% { opacity: 1; }
    68% { opacity: 0; }
    88% { opacity: 0; }
    100% { opacity: 1; }
  }
</style>
